import React, { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';

import AampeTooltip from 'components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';
import routes from '../../constants/routes';

import useEventsQuery from '../../hooks/events/useEventsQuery';
import useFormulasCategoryCountQuery from '../../hooks/formulas/useFormulasCategoryCount';

import ChannelMixDistribution from './ChannelMixDistribution';
import SummarySlide from './SummarySlide';
import TopActionCard from './TopActionCard';
import TopPerformingTab from './TopPerformingTab';
import { TopPerformingTabs } from './constant';
import HeroRecommendationsWidget from './heroRecommendations/HeroRecommendationsWidget';

interface CardFooterConfig {
    label: string;
    route: string;
}

type CardSpan = 1 | 2 | 3 | 4 | 5 | 6;

const DashboardCards: FC<React.PropsWithChildren> = () => {
    const { data: eventsList } = useEventsQuery(false);

    const { data: formulasCount } = useFormulasCategoryCountQuery();

    const uncategorisedEventsCount = useMemo<number>(() => {
        return eventsList?.filter((event) => !event.context)?.length ?? 0;
    }, [eventsList]);

    const hasLiveMessages = useMemo<boolean>(() => {
        if (formulasCount) {
            const liveMessages = formulasCount.Live;
            const liveMessagesCount = Object.values(liveMessages).reduce(
                (acc, count) => acc + count,
                0,
            );
            return liveMessagesCount > 0;
        }
        return false;
    }, [formulasCount]);

    const topCards = useMemo(() => {
        const baseCards: {
            order: number;
            title: string;
            titleTag?: React.ReactNode;
            rightAlignInfoTag?: boolean;
            description?: React.ReactNode;
            icon?: React.ReactNode;
            link?: string;
            route?: string;
            Component: React.ReactNode;
            footer?: CardFooterConfig;
        }[] = [
            {
                order: 1,
                title: 'Quick summary',
                Component: <SummarySlide />,
            },

            {
                order: 5,
                title: 'Top messages (Last 30 days)',
                description: (
                    <>
                        Your best performing messages will be <br /> displayed
                        here.
                    </>
                ),
                icon: <ICONS.pencil />,
                link: 'Create your first message',
                route: routes.formulasCreate,
                Component: (
                    <TopPerformingTab tab={TopPerformingTabs.TopMessages} />
                ),
            },
            {
                order: 6,
                title: 'Top labels (Last 30 days)',
                description: (
                    <>
                        Your best performing labels will be <br /> displayed
                        here.
                    </>
                ),
                icon: <ICONS.pencil />,
                link: 'Create your first label',
                route: routes.labelManagement,
                Component: (
                    <TopPerformingTab tab={TopPerformingTabs.TopLabels} />
                ),
            },
        ];
        if (hasLiveMessages) {
            baseCards.push({
                order: uncategorisedEventsCount ? 4 : 3,
                title: 'Channel mix',
                titleTag: (
                    <LiveMessageTagContainer>
                        <LiveMessageTag>Live Messages</LiveMessageTag>
                    </LiveMessageTagContainer>
                ),
                description: (
                    <>
                        Your messages channel distribution will be <br />
                        displayed here.
                    </>
                ),
                icon: <ICONS.pencil />,
                link: 'Create your first message',
                route: routes.formulasCreate,
                Component: <ChannelMixDistribution />,
            });
        }
        baseCards.push({
            order: uncategorisedEventsCount ? 3 : 2,
            title: 'Content coverage opportunities',
            titleTag: (
                <AampeTooltip
                    title="These label pairs have the highest potential for impact based on their performance. Unlock more learning by adding alternates."
                    placement="left"
                    arrow
                >
                    <span>
                        <ICONS.info size={16} />
                    </span>
                </AampeTooltip>
            ),
            rightAlignInfoTag: true,
            Component: <HeroRecommendationsWidget />,
        });

        if (uncategorisedEventsCount) {
            baseCards.push({
                order: 2,
                title: 'Actions for you',
                Component: <TopActionCard count={uncategorisedEventsCount} />,
            });
        }
        return baseCards.sort((a, b) => a.order - b.order);
    }, [uncategorisedEventsCount, hasLiveMessages]);

    /**
     * Computes the grid column span for a card so that all rows fill 6 columns.
     *
     * Layout rules (total cards → row sizes):
     * - 6 cards → [3, 3] — all cards span 2 each
     * - 5 cards → [2, 3] — row 1: 2 cards span 3, row 2: 3 cards span 2
     * - 4 cards → [2, 2] — all cards span 3 each
     * - 3 cards → [2, 1] — row 1: 2 cards span 3, row 2: 1 card spans 6
     *
     * @param index - Zero-based position of the card in the sorted list.
     * @param total - Total number of cards being rendered.
     * @returns The number of grid columns the card should span.
     */
    const getCardSpan = (index: number, total: number): CardSpan => {
        if (total === 6) return 2;

        if (index === 0) return 2;
        if (index === 1) return 4;

        if (index < 2) return 3;

        const remaining = total - 2;
        if (remaining === 1) return 6;
        if (remaining === 2) return 3;

        return 2;
    };

    return (
        <Container className="hide-scroll-bar">
            {topCards.map((card, index) => (
                <Card
                    key={card.title}
                    $span={getCardSpan(index, topCards.length)}
                >
                    <CardTitle $rightAlignInfoTag={card.rightAlignInfoTag}>
                        <CardTitleText>{card.title}</CardTitleText>
                        {card.titleTag && (
                            <CardTitleTag $rightAlign={card.rightAlignInfoTag}>
                                {card.titleTag}
                            </CardTitleTag>
                        )}
                    </CardTitle>
                    <CardBody className="hide-scroll-bar">
                        {card.Component}
                    </CardBody>
                    {card.footer && (
                        <CardFooter>
                            <StyledLink to={card.footer.route}>
                                {card.footer.label}
                            </StyledLink>
                        </CardFooter>
                    )}
                </Card>
            ))}
        </Container>
    );
};

const Container = styled.div(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gridAutoRows: 'minmax(320px, 1fr)',
    height: '100%',
    gap: aampeTheme.spacing4(5),
    '@media (max-width: 1380px)': {
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    },
    '@media (max-width: 1080px)': {
        gridTemplateColumns: '1fr',
        gridAutoRows: 'auto',
        height: 'auto',
    },
}));

const Card = styled.div<{ $span: CardSpan }>(({ $span }) => ({
    gridColumn: `span ${$span}`,
    background: aampeTheme.colors.white,
    borderRadius: aampeTheme.borderRadius.default,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '@media (max-width: 1380px)': {
        gridColumn: 'span 2',
    },
    '@media (max-width: 1080px)': {
        gridColumn: '1 / -1',
    },
}));

const CardTitle = styled.p<{ $rightAlignInfoTag?: boolean }>(
    ({ $rightAlignInfoTag }) => ({
        display: 'flex',
        alignItems: $rightAlignInfoTag ? 'flex-start' : 'center',
        gap: aampeTheme.spacing4(2),
        color: aampeTheme.colors.neutral09,
        fontSize: aampeTheme.size.font.text,
        fontWeight: aampeTheme.weights.heavy,
        padding: `${aampeTheme.spacing4(4)} ${aampeTheme.spacing4(6)} 0`,
        flexShrink: 0,
    }),
);

const CardTitleText = styled.span(() => ({
    minWidth: 0,
}));

const CardTitleTag = styled.span<{ $rightAlign?: boolean }>(
    ({ $rightAlign }) => ({
        display: 'inline-flex',
        marginLeft: $rightAlign ? 'auto' : 0,
        alignSelf: $rightAlign ? 'flex-start' : 'center',
    }),
);

const CardBody = styled.div(() => ({
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: aampeTheme.spacing4(6),
}));

const CardFooter = styled.div(() => ({
    borderTop: `1px solid ${aampeTheme.colors.blueGray50}`,
    padding: `${aampeTheme.spacing4(3)} ${aampeTheme.spacing4(6)}`,
    flexShrink: 0,
}));

const StyledLink = styled(Link)(() => ({
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.default,
    paddingTop: aampeTheme.spacing(1),
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    fontWeight: aampeTheme.weights.medium,
}));

const LiveMessageTagContainer = styled.div({
    padding: aampeTheme.spacing4(0.5, 1),
    borderRadius: aampeTheme.borderRadius.default,
    background: aampeTheme.colors.green2,
});

const LiveMessageTag = styled.span({
    textTransform: 'uppercase',
    fontSize: aampeTheme.size.font.textSmall,
    color: aampeTheme.colors.white,
});

export default DashboardCards;
